import { useNavigate } from 'react-router-dom'

import * as spriteIcons from 'assets/svg/sprite'
import SelectCategoriesModal from 'components/UI/Modal/SelectCategoriesModal/SelectCategoriesModal'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import style from 'pages/vault/_vault.module.scss'
import {
  type ClientSortKey,
} from 'pages/vault/components/VaultCardGrouped'
import { type ICategory } from 'types/interfaces/ITypes'

import {
  type MediaType,
  buildFeedFilter,
  feedSort,
  messagesSort,
} from '../vault.constants'

type Props = {
  type: string
  id: any

  activeFilter: string
  setActiveFilter: (value: string) => void

  postsGrouped: string | boolean
  postsFilter: MediaType
  showDuplicates: boolean
  unreleasedOnly: boolean

  postsSort: ClientSortKey
  postsAsc: string

  setPostsSort: React.Dispatch<React.SetStateAction<ClientSortKey>>
  setPostsAsc: (value: string) => void

  setPostsGrouped: (value: any) => void
  setPostsFilter: (value: MediaType) => void
  setSelectShowDuplicates: (value: any) => void
  setShowDuplicates: (value: boolean) => void
  setUnreleasedOnly: (value: boolean) => void

  priceOnly: boolean
  setPriceOnly: (value: boolean) => void

  activeCategory: any[]
  setActiveCategory: (value: any) => void

  allTimeStats: boolean
  setAllTimeStats: (value: boolean) => void

  userData: any
}

type VaultFilterToggles = {
  group_by_post: boolean
  show_duplicates: boolean
  unreleased_only: boolean
  all_time_stats: boolean
  price: boolean
  mass_messages: boolean
}

export function VaultHeader(props: Props) {
  const {
    type,
    id,

    postsGrouped,
    postsFilter,
    showDuplicates,
    unreleasedOnly,

    postsSort,
    postsAsc,

    activeFilter,
    setActiveFilter,

    setPostsSort,
    setPostsAsc,

    setPostsGrouped,
    setPostsFilter,
    setSelectShowDuplicates,
    setShowDuplicates,
    setUnreleasedOnly,

    priceOnly,
    setPriceOnly,

    allTimeStats,
    setAllTimeStats,

    activeCategory,
    setActiveCategory,
  } = props

  const feedFilter = buildFeedFilter()

  const isMainTab =
    type === 'feed' ||
    type === 'premium' ||
    type === 'messages' ||
    type === 'mass_messages' ||
    type === 'story' ||
    type === 'all'

  const sortElements =
    type === 'all' ||
    type === 'feed' ||
    type === 'premium'
      ? feedSort
      : messagesSort

  const navigate = useNavigate()
  const modalData = useModalContext()

  const setVaultType = (nextType: string) => {
    setActiveFilter(nextType)

    navigate(
      !id
        ? `/vault?type=${nextType}`
        : `/vault/${id}?type=${nextType}`
    )
  }

const applyVaultFilter = (
  _primary: string | boolean,
  secondary: string,
  _selectProps: any,
  toggles: VaultFilterToggles
) => {
  const massMessagesEnabled = !!toggles.mass_messages

  /*
   * Mass Messages is exclusive with:
   * - Group by Post | Message
   * - Group Duplicates
   * - Unreleased Only
   */
  const groupByPost = massMessagesEnabled
    ? false
    : !!toggles.group_by_post

  const groupDuplicates = massMessagesEnabled
    ? false
    : !!toggles.show_duplicates

  const unreleasedEnabled = massMessagesEnabled
    ? false
    : !!toggles.unreleased_only

  /*
   * Internally `showDuplicates` has the opposite meaning from the
   * "Group Duplicates" toggle:
   *
   * Group Duplicates ON  -> showDuplicates false
   * Group Duplicates OFF -> showDuplicates true
   */
  const nextShowDuplicates = !groupDuplicates

  setPostsGrouped(groupByPost)
  setPostsFilter((secondary || 'all_media') as MediaType)

  setSelectShowDuplicates(
    nextShowDuplicates
      ? 'show_duplicates'
      : ''
  )

  setShowDuplicates(nextShowDuplicates)

  setAllTimeStats(
    !massMessagesEnabled &&
    groupDuplicates &&
    !unreleasedEnabled
      ? !!toggles.all_time_stats
      : false
  )

  if (massMessagesEnabled) {
    setVaultType('mass_messages')
  } else if (activeFilter === 'mass_messages') {
    setVaultType('messages')
  }

  setPriceOnly(!!toggles.price)
  setUnreleasedOnly(unreleasedEnabled)
}
const isMassMessagesActive = activeFilter === 'mass_messages'

const filterButton = {
  type: 'filter',
  elements: feedFilter,
  sortingProps: {
    selectedSort: isMassMessagesActive
      ? ''
      : postsGrouped
        ? 'group_by_post'
        : '',

    selectedOrder: postsFilter,

    selectedShowDuplicates: isMassMessagesActive
      ? false
      : !showDuplicates,

    unreleasedOnly: isMassMessagesActive
      ? false
      : unreleasedOnly,

    initialToggles: {
      /*
       * Mass Messages takes priority when this tab is active.
       * This prevents the modal from opening with both sides of
       * the exclusive relationship enabled.
       */
      mass_messages: isMassMessagesActive,

      group_by_post: isMassMessagesActive
        ? false
        : !!postsGrouped,

      show_duplicates: isMassMessagesActive
        ? false
        : !showDuplicates,

      unreleased_only: isMassMessagesActive
        ? false
        : !!unreleasedOnly,

      all_time_stats: isMassMessagesActive
        ? false
        : !!allTimeStats,

      price: !!priceOnly,
    },
  },
  applyFn: applyVaultFilter,
}
  const buttons = isMainTab
    ? [
        {
          type: 'sort',
          elements: sortElements,
          sortingProps: {
            selectedSort: postsSort || 'most_recent',
            selectedOrder: postsAsc || 'desc',
          },
        applyFn: (
          selectedSort: ClientSortKey,
          selectedOrder: 'asc' | 'desc'
        ) => {
          setPostsSort(selectedSort || 'most_recent')
          setPostsAsc(selectedOrder || 'desc')
        },
        },
        filterButton,
      ]
    : [filterButton]

  return (
    <LayoutHeader
      type='title-with-buttons'
      buttons={buttons}
      titleWithIcon={
        <div
          className={style.select_category_container}
          onClick={() => {
            modalData.addModal(
              'Select category',
<SelectCategoriesModal
    selectedCategories={activeCategory}
    onlyOne
    onSave={(value: ICategory[]) => {
        if (value.length > 0 && value[0].id) {
            setActiveCategory(value)
        } else {
            setActiveCategory([
                {
                    name: 'All',
                    id: null,
                    posts: 0,
                    premium: 0,
                    stories: 0,
                    messages: 0,
                    selected: true,
                },
            ])
        }

        modalData.clearModal()
    }}
    onCancel={() => modalData.clearModal()}
/>,
              false,
              true,
              'compact-modal'
            )
          }}
        >
          <div className={style.select_category_icon}>
            <spriteIcons.IconHamburgerLinesWhite />
          </div>

          <div className={style.select_category_text}>
            {activeCategory?.[0]?.name}
          </div>
        </div>
      }
    />
  )
}